import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Drawer, Filter, FilterSingle, Button } from '@unite-us/app-components';
import { useFind } from 'src/api/APIHooks';
import { retrieveTypeAheadCountiesRaw } from 'src/common/utils/Geographic/geographicalsTypeAhead';
import {
  useSocialNeeds,
  useEnhancedPopulations,
  useTasksSubmittingProviders,
} from './tasksQueries';

const getCountyOptionValue = (county) => `${county.id}::${county.document?.name}`;
let lastCountySearchOptions = [];

const ScreeningFiltersDrawer = ({
  isOpen,
  onClose,
  onReset,
  submittingOrgFilter,
  setSubmittingOrgFilter,
  countyFilter,
  setCountyFilter,
  countyOptions,
  languageFilter,
  setLanguageFilter,
  identifiedNeedsFilter,
  setIdentifiedNeedsFilter,
  identifiedNeedsCountFilter,
  setIdentifiedNeedsCountFilter,
  enhancedPopulationsFilter,
  setEnhancedPopulationsFilter,
}) => {
  const languagesQuery = useFind(
    'languages',
    {},
    {
      queryConfig: {
        enabled: isOpen,
        placeholderData: undefined,
      },
      sort: 'name',
    },
  );
  const {
    data: socialNeedsOptions = [],
    isLoading: loadingIdentifiedNeeds,
  } = useSocialNeeds({ enabled: isOpen });
  const {
    data: initiativeConditionsOptions = [],
    isLoading: loadingEnhancedPopulations,
  } = useEnhancedPopulations({ enabled: isOpen });
  const {
    data: tasksSubmittingProvidersResponse,
    isLoading: loadingSubmittingOrganizations,
  } = useTasksSubmittingProviders({}, { enabled: isOpen });
  const { data: languagesData, isFetching: loadingLanguages } = languagesQuery;
  const submittingProviders = get(tasksSubmittingProvidersResponse, 'data', []);
  const submittingOrganizationOptions = submittingProviders
    .filter((provider) => provider?.id && provider?.name)
    .map((provider) => ({
      value: provider.id,
      label: provider.name,
      initial: submittingOrgFilter.includes(provider.id),
    }));

  const identifiedNeedsOptions = socialNeedsOptions.map((option) => ({
    ...option,
    initial: identifiedNeedsFilter.includes(option.value),
  }));
  const enhancedPopulationsOptions = initiativeConditionsOptions.map((option) => ({
    ...option,
    initial: enhancedPopulationsFilter.includes(option.value),
  }));
  const identifiedNeedsCountOptions = Array.from({ length: 15 }, (_, index) => {
    const value = String(index + 1);
    return {
      value,
      label: value,
      initial: identifiedNeedsCountFilter.includes(value),
    };
  });
  const languageOptions = get(languagesData, 'data.data', []).map((language) => ({
    value: language.id,
    label: language.name || language.id,
    initial: languageFilter.includes(language.id),
  }));
  const resolvedCountyOptions = countyOptions.length > 0 ? countyOptions : lastCountySearchOptions;
  const searchCountyOptions = async (searchText) => {
    const trimmedSearchText = searchText?.trim();
    if (!trimmedSearchText) {
      return resolvedCountyOptions;
    }

    const counties = await retrieveTypeAheadCountiesRaw(trimmedSearchText);
    const mappedCountyOptions = counties.map((county) => ({
      value: getCountyOptionValue(county),
      label: county.document?.full_name,
      initial: countyFilter.includes(getCountyOptionValue(county)),
    }));

    lastCountySearchOptions = mappedCountyOptions;
    return mappedCountyOptions;
  };
  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      secondary
      width="500px"
      topOffset="60px"
      className="border border-solid border-medium-border-blue"
      id="tasks-v3-filters-drawer"
    >
      <div className="h-full p-6 bg-light-fill-grey">
        <div className="flex items-center justify-between gap-4 pb-6">
          <h3 className="font-semibold leading-7">Filters</h3>
          <Button
            omitDefaultStyles
            className="px-0 py-0 text-sm text-action-blue leading-5"
            onClick={onReset}
            label="Reset Filters"
          />
        </div>

        <div className="flex flex-col gap-4">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Submitting Organization
            </p>
            <Filter
              id="drawer-filter-submitting-org"
              className="w-full min-w-0 max-w-none"
              name={loadingSubmittingOrganizations ? 'Loading Submitting Organizations...' : 'Submitting Organization'}
              pluralName="Submitting Organizations"
              filterKey="submitting_org"
              options={submittingOrganizationOptions}
              onFiltersChange={setSubmittingOrgFilter}
              isActive={submittingOrgFilter.length > 0}
            />
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              County
            </p>
            <Filter
              id="drawer-filter-county"
              className="w-full min-w-0 max-w-none"
              name="County"
              pluralName="Counties"
              filterKey="county"
              options={resolvedCountyOptions}
              asyncSearch={searchCountyOptions}
              asyncLoadingText="Loading Counties..."
              onFiltersChange={setCountyFilter}
              isActive={countyFilter.length > 0}
            />
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Language
            </p>
            <div className="w-full min-w-0 max-w-none">
              <FilterSingle
                id="drawer-filter-language"
                className="w-full max-w-none min-w-0"
                name={loadingLanguages ? 'Loading Languages...' : 'Language'}
                placeholder="Language"
                options={languageOptions}
                onFilterChange={(activeFilter) => setLanguageFilter(activeFilter?.value ? [activeFilter.value] : [])}
                value={languageFilter[0] ?? ''}
              />
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Identified Needs
            </p>
            <Filter
              id="drawer-filter-identified-needs"
              className="w-full min-w-0 max-w-none"
              name={loadingIdentifiedNeeds ? 'Loading Identified Needs...' : 'Identified Need'}
              pluralName="Identified Needs"
              filterKey="screening_identified_needs"
              options={identifiedNeedsOptions}
              onFiltersChange={setIdentifiedNeedsFilter}
              isActive={identifiedNeedsFilter.length > 0}
            />
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Identified Needs Count
            </p>
            <Filter
              id="drawer-filter-identified-needs-count"
              className="w-full min-w-0 max-w-none"
              name="Identified Needs Count"
              pluralName="Identified Needs Counts"
              filterKey="screening_identified_needs_count"
              options={identifiedNeedsCountOptions}
              onFiltersChange={setIdentifiedNeedsCountFilter}
              isActive={identifiedNeedsCountFilter.length > 0}
            />
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Enhanced Populations
            </p>
            <Filter
              id="drawer-filter-enhanced-populations"
              className="w-full min-w-0 max-w-none"
              name={loadingEnhancedPopulations ? 'Loading Enhanced Populations...' : 'Enhanced Population'}
              pluralName="Enhanced Populations"
              filterKey="enhanced_populations"
              options={enhancedPopulationsOptions}
              onFiltersChange={setEnhancedPopulationsFilter}
              isActive={enhancedPopulationsFilter.length > 0}
            />
          </div>
        </div>
      </div>
    </Drawer>
  );
};

ScreeningFiltersDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  submittingOrgFilter: PropTypes.array,
  setSubmittingOrgFilter: PropTypes.func.isRequired,
  countyFilter: PropTypes.array,
  setCountyFilter: PropTypes.func.isRequired,
  countyOptions: PropTypes.array,
  languageFilter: PropTypes.array,
  setLanguageFilter: PropTypes.func.isRequired,
  identifiedNeedsFilter: PropTypes.array,
  setIdentifiedNeedsFilter: PropTypes.func.isRequired,
  identifiedNeedsCountFilter: PropTypes.array,
  setIdentifiedNeedsCountFilter: PropTypes.func.isRequired,
  enhancedPopulationsFilter: PropTypes.array,
  setEnhancedPopulationsFilter: PropTypes.func.isRequired,
};

ScreeningFiltersDrawer.defaultProps = {
  submittingOrgFilter: [],
  countyFilter: [],
  countyOptions: [],
  languageFilter: [],
  identifiedNeedsFilter: [],
  identifiedNeedsCountFilter: [],
  enhancedPopulationsFilter: [],
};

export default ScreeningFiltersDrawer;
